import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/outline';
import get from 'lodash.get';
import React, { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Pagination } from '../Pagination';
import { Text, TextType } from '../Text';
import { appendClassProps } from '../util';
import { GridTableSortDirection } from './index.types';
const GridTableCell = ({ children, showRowLines = true, wrap, className, onClick }) => {
    // TODO: Conditionally apply title
    // https://codesandbox.io/s/material-demo-p2omr?file=/demo.js
    return (React.createElement("div", { className: `td${showRowLines ? ' border-t border-gray-300' : ''} ${onClick ? ' cursor-pointer' : ''}${wrap ? ' wrap' : ''}${appendClassProps(className)}`, onClick: onClick, title: typeof children === 'string' && !wrap ? children : undefined }, children));
};
/**
 - GridTable component for rendering a page of data
 - Can provide any array of data objects and the table will only render the columns provided in the column prop
 */
export const GridTable = ({ showHeader = true, showRowLines = true, highlightRows = true, className, columns, rows, pageNumber, countPerPage, totalCount, loading, onClickRow, onChangeCountPerPage, onChangePageNumber, onChangeColumns }) => {
    const handleChangeCountPerPage = (count) => {
        onChangeCountPerPage(parseInt(count));
    };
    const handleChangePageNumber = (page) => {
        onChangePageNumber(parseInt(page));
    };
    const handleClickColumn = (id) => {
        const newColumns = [...columns];
        for (let i = 0; i < newColumns.length; i++) {
            if (newColumns[i].id === id) {
                newColumns[i].sortDirection =
                    newColumns[i].sortDirection === GridTableSortDirection.down ||
                        !newColumns[i].sortDirection
                        ? GridTableSortDirection.up
                        : GridTableSortDirection.down;
            }
            else {
                delete newColumns[i].sortDirection;
            }
        }
        onChangeColumns(newColumns);
    };
    const tableBody = useMemo(() => rows.map((row, rowIdx) => (React.createElement("div", { className: `row${highlightRows ? ' highlight-row' : ''}`, key: row.id }, columns.map((column, colIdx) => {
        let value = get(row, column.id);
        let content;
        if (typeof value === 'string' || typeof value === 'number') {
            content = (React.createElement(Text, { type: TextType.custom, className: "text-sm", key: column.id, wrap: column.wrap }, value));
        }
        else if (React.isValidElement(value)) {
            content = React.createElement(React.Fragment, { key: column.id }, value);
        }
        else {
            content = value ? (React.createElement(Text, { type: TextType.custom, className: "text-sm", key: column.id, wrap: column.wrap }, JSON.stringify(value))) : null;
        }
        return (React.createElement(GridTableCell, { className: `py-2 ${colIdx === 0 ? 'pl-4' : 'pl-2'} ${colIdx === columns.length - 1 ? 'pr-4' : 'pr-2'} relative`, key: column.id, showRowLines: showRowLines && rowIdx !== 0, onClick: onClickRow
                ? () => {
                    const highlightedText = window
                        ?.getSelection()
                        ?.toString();
                    if (onClickRow && !highlightedText)
                        onClickRow(row.id);
                }
                : undefined, wrap: column.wrap },
            React.createElement("div", { className: "h-full relative" },
                loading && (React.createElement("div", { className: "absolute top-0 left-0 right-0 bottom-0 z-0" },
                    React.createElement(Skeleton, { containerClassName: "h-full", className: "h-full !leading-10" }))),
                React.createElement("span", { className: loading ? 'invisible' : '' }, content))));
    })))), [rows, columns, showRowLines, loading, highlightRows]);
    let loadingGridTableBody = [];
    if (loading) {
        for (let i = 0; i < countPerPage; i++) {
            loadingGridTableBody.push(React.createElement("div", { className: "loading-row", style: {
                    gridColumn: `1 / ${columns.length + 1}`
                }, key: i },
                React.createElement(Skeleton, { height: 36.5, style: { zIndex: 0 } })));
        }
    }
    return (React.createElement("div", { className: `grid-table relative w-full bg-gray-50${appendClassProps(className)}`, "data-testid": "table", style: {
            gridTemplateColumns: `${columns
                // .map((col) => (col.isContentColumn ? '1fr' : 'auto'))
                .map(() => '1fr')
                .join(' ')}`
        } },
        showHeader &&
            columns.map((column, i) => (React.createElement("div", { className: `thead z-10 text-blue-800 bg-gray-300 cursor-pointer py-2 ${i === 0 ? 'pl-4' : 'pl-2'} ${i === columns.length - 1 ? 'pr-4' : 'pr-2'}`, key: column.id, onClick: () => handleClickColumn(column.id) },
                column.label && (React.createElement(Text, { type: TextType.custom, className: "text-sm", wrap: true }, column.label)),
                column.sortDirection === GridTableSortDirection.up && (React.createElement(ChevronUpIcon, { className: "h-5 w-5 overflow-visible" })),
                column.sortDirection === GridTableSortDirection.down && (React.createElement(ChevronDownIcon, { className: "h-5 w-5 overflow-visible" })),
                !column.sortDirection && (React.createElement(ChevronDownIcon, { className: "h-5 w-5 overflow-visible opacity-0" }))))),
        loading && (!rows || rows.length < 1) && loadingGridTableBody,
        tableBody,
        React.createElement("div", { className: "tfoot bg-gray-50", style: {
                gridColumn: `1 / ${columns.length + 1}`
            } },
            React.createElement(Pagination, { pageNumber: pageNumber, countPerPage: countPerPage, totalCount: totalCount, onChangeCountPerPage: handleChangeCountPerPage, onChangePageNumber: (page) => handleChangePageNumber(page.toString()), loading: loading }))));
};
export * from './index.types';
//# sourceMappingURL=index.js.map